import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { exportAnnotatedMarkup } from './markupExport';
import { isPdfType } from './markupSource';
import '../PdfExportDialog.css';

/**
 * "Export annotated PDF" dialog (BBD-126), opened from the markup editor and
 * from the markup list.
 *
 * markup: { id, file_name, file_type } of the document to export.
 * loaded: optional { document, fileBytes, knownStrokes } when the caller (the
 *   editor) already holds the file and some of its pages, so they are not
 *   fetched again and unsaved strokes are included.
 */
function MarkupExportDialog({ markup, loaded = null, onClose }) {
    const isPdf = isPdfType(markup.file_type);
    const [scope, setScope] = useState('all');
    const [status, setStatus] = useState({ state: 'idle', message: '' });
    const [legacyConverted, setLegacyConverted] = useState(0);

    const working = status.state === 'working';

    const handleExport = async () => {
        setStatus({ state: 'working', message: 'Creating your PDF...' });
        setLegacyConverted(0);
        try {
            const result = await exportAnnotatedMarkup({
                documentId: markup.id,
                document: loaded?.document || null,
                fileBytes: loaded?.fileBytes || null,
                knownStrokes: loaded?.knownStrokes || {},
                onlyAnnotatedPages: isPdf && scope === 'annotated',
            });
            setLegacyConverted(result.legacyConverted);
            setStatus({ state: 'done', message: `Saved "${result.fileName}".` });
        } catch (err) {
            console.error('Annotated PDF export failed:', err);
            setStatus({
                state: 'error',
                message: err?.name === 'MarkupExportError'
                    ? err.message
                    : 'The annotated PDF could not be created. Check your connection and try again.',
            });
        }
    };

    const close = () => {
        if (!working) onClose();
    };

    return createPortal(
        <div className="NotebookPdfExport__overlay" onClick={close}>
            <div
                className="NotebookPdfExport__dialog"
                role="dialog"
                aria-modal="true"
                aria-label="Export annotated PDF"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="NotebookPdfExport__header">
                    <h2>Export annotated PDF</h2>
                    <button className="NotebookPdfExport__close" onClick={close} disabled={working} aria-label="Close">
                        &times;
                    </button>
                </div>

                <div className="NotebookPdfExport__body">
                    <p className="NotebookPdfExport__name" title={markup.file_name}>{markup.file_name}</p>

                    {isPdf ? (
                        <fieldset className="NotebookPdfExport__fieldset" disabled={working}>
                            <label className="NotebookPdfExport__choice">
                                <input
                                    type="radio"
                                    name="markup-export-scope"
                                    checked={scope === 'all'}
                                    onChange={() => setScope('all')}
                                />
                                All pages
                            </label>
                            <label className="NotebookPdfExport__choice">
                                <input
                                    type="radio"
                                    name="markup-export-scope"
                                    checked={scope === 'annotated'}
                                    onChange={() => setScope('annotated')}
                                />
                                Only pages that have annotations
                            </label>
                        </fieldset>
                    ) : (
                        <p className="NotebookPdfExport__hint">
                            The image becomes a one-page PDF with your annotations on top.
                        </p>
                    )}

                    {isPdf && (
                        <p className="NotebookPdfExport__hint">
                            Your annotations are added on top of the original PDF. Its text stays selectable.
                        </p>
                    )}

                    {status.message && (
                        <p className={`NotebookPdfExport__status NotebookPdfExport__status--${status.state}`} role="status">
                            {status.message}
                        </p>
                    )}

                    {legacyConverted > 0 && (
                        <p className="NotebookPdfExport__status NotebookPdfExport__status--warning">
                            {legacyConverted} older annotation{legacyConverted !== 1 ? 's were' : ' was'} saved
                            without a zoom level and placed by estimate. Check that {legacyConverted !== 1 ? 'they line' : 'it lines'} up
                            in the exported file.
                        </p>
                    )}
                </div>

                <div className="NotebookPdfExport__footer">
                    <button className="NotebookPdfExport__btn" onClick={close} disabled={working}>
                        {status.state === 'done' ? 'Close' : 'Cancel'}
                    </button>
                    <button className="NotebookPdfExport__btn NotebookPdfExport__btn--primary" onClick={handleExport} disabled={working}>
                        {working
                            ? <><i className="fas fa-spinner fa-spin"></i> Exporting...</>
                            : <><i className="fas fa-file-pdf"></i> Export</>}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
}

export default MarkupExportDialog;
